// VacancyReviewScreen.jsx - FIXED CHAT TOGGLE LOGIC

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import './VacancyReviewScreen.css';
import { useAuth0 } from '@auth0/auth0-react';
import { parseVacancyToSections } from '../utils/vacancyUtils';
import { notify } from '../utils/notifications';
import avatarImg from '../images/Jesse_avatar.webp';

// NIEUW: Component om de AI-berichten te formatteren
const FormattedMessageContent = ({ content }) => {
  const lines = content.split('\n').filter(line => line.trim() !== '');

  return (
    <div>
      {lines.map((line, index) => {
        // Herken **Header**
        if (line.startsWith('**') && line.endsWith('**')) {
          return <h4 key={index} className="chat-message-header">{line.substring(2, line.length - 2)}</h4>;
        }
        // Herken bullet points
        if (line.trim().startsWith('•')) {
          return <li key={index} className="chat-message-bullet">{line.trim().substring(1).trim()}</li>;
        }
        // Normale paragraaf
        return <p key={index}>{line}</p>;
      })}
    </div>
  );
};

const VacancyReviewScreen = ({
  vacancyText,
  isVisible,
  onClose,
  onChatOpen,
  onVacancyUpdated
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [formattedVacancy, setFormattedVacancy] = useState(null);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  // Draggable + resizable chat panel
  const [chatPos, setChatPos] = useState({ x: 0, y: 0 });
  const [chatHeight, setChatHeight] = useState(380);
  const CHAT_MIN_H = 280;
  const CHAT_MAX_H = Math.round(window.innerHeight * 0.8);
  const CHAT_WIDTH = 480; // matches CSS min(480px, 92vw)
  const dragRef = useRef(null);
  const interactionMode = useRef(null); // 'drag' | 'resize'
  const interactionStart = useRef({ x: 0, y: 0, h: 0 });

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    interactionMode.current = 'drag';
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    interactionStart.current = { x: cx - chatPos.x, y: 0, h: 0 };
  }, [chatPos]);

  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    interactionMode.current = 'resize';
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    interactionStart.current = { x: 0, y: cy, h: chatHeight };
  }, [chatHeight]);

  useEffect(() => {
    const onMove = (e) => {
      if (!interactionMode.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      if (interactionMode.current === 'drag') {
        const rawX = cx - interactionStart.current.x;
        // Panel is centered (left:50%, translate -50%), so x=0 means centered.
        // Clamp so the panel edge stays within the viewport with 20px margin.
        const panelW = Math.min(CHAT_WIDTH, window.innerWidth * 0.92);
        const maxOffset = (window.innerWidth - panelW) / 2 - 20;
        setChatPos(prev => ({
          ...prev,
          x: Math.max(-maxOffset, Math.min(maxOffset, rawX)),
        }));
      } else if (interactionMode.current === 'resize') {
        const delta = interactionStart.current.y - cy;
        const newH = Math.min(CHAT_MAX_H, Math.max(CHAT_MIN_H, interactionStart.current.h + delta));
        setChatHeight(newH);
      }
    };
    const onEnd = () => {
      interactionMode.current = null;
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [CHAT_MAX_H]);

  // Parse en format de vacaturetekst
  useEffect(() => {
    if (vacancyText && isVisible) {
      const formatted = parseVacancyText(vacancyText);
      setFormattedVacancy(formatted);
      setIsAnimating(true);
      
      // Reset animatie na completion
      setTimeout(() => setIsAnimating(false), 800);
    }
  }, [vacancyText, isVisible]);

  // Strip markdown markers (**, *, etc.) from text
  const stripMarkdown = (text) => text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');

  // Capitalize first letter of each word
  const titleCase = (str) => str.replace(/\b\w/g, c => c.toUpperCase());

  const parseVacancyText = (text) => {
    if (!text) return {};

    const sections = {};
    const headerPatterns = [
        /^(wat ga je doen|werkzaamheden|taken en verantwoordelijkheden)[:.]?\s*$/i,
        /^(wie ben jij|functie-eisen|eisen|vereisten)[:.]?\s*$/i,
        /^(wat bieden wij|arbeidsvoorwaarden|ons aanbod|voordelen)[:.]?\s*$/i,
        /^(waarom zou je|waarom wil je|voordelen van deze functie)[:.]?\s*$/i,
        /^(intro|inleiding|over de functie)[:.]?\s*$/i
    ];

    const lines = text.split('\n').filter(line => line.trim());
    let currentSection = 'intro';
    let currentContent = [];

    lines.forEach((line, index) => {
        const trimmed = stripMarkdown(line.trim());
        if (!trimmed) return;

        const isHeader = headerPatterns.some(pattern => pattern.test(trimmed)) ||
                        (trimmed.length < 80 && trimmed.endsWith(':')) ||
                        (trimmed.length < 60 && !trimmed.includes('.') && trimmed.split(' ').length <= 6);

        if (isHeader && index > 0) {
        if (currentContent.length > 0) {
            sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = trimmed.replace(/[:.]$/, '').toLowerCase();
        currentContent = [];
        } else {
        currentContent.push(trimmed);
        }
    });

    if (currentContent.length > 0) {
        sections[currentSection] = currentContent.join('\n').trim();
    }

    return sections;
  };

  // FIXED: Chat toggle functie
  const toggleChatPanel = () => {
    console.log('🎨 Toggling chat panel. Current state:', showChatPanel);
    
    if (!showChatPanel) {
        // OPEN CHAT
        openChat();
    } else {
        // CLOSE CHAT  
        closeChat();
    }
    };

  // NIEUWE: Open chat functie
    const openChat = () => {
    console.log('Opening chat panel');
    
    // Initialiseer chat als nog niet gedaan
    if (!chatInitialized) {
        setChatMessages([{
        role: 'assistant',
        content: 'Wat vind je van de vacaturetekst? Welke aanpassingen wil je maken?',
        timestamp: Date.now()
        }]);
        setChatInitialized(true);
    }
    
    // Set context for chat
    window.fullVacancyForChat = vacancyText;
    
    // Reset position/size and show chat panel
    setChatPos({ x: 0, y: 0 });
    setChatHeight(380);
    setShowChatPanel(true);
    };

    // NIEUWE: Close chat functie
    const closeChat = () => {
    console.log('Closing chat panel');
    setShowChatPanel(false);
    
    // Reset chat state na animatie
    setTimeout(() => {
        setChatInitialized(false);
        setChatMessages([]);
    }, 400);
    };

  // Rest van je bestaande functies blijven hetzelfde...
  const formatContent = (content) => {
    if (!content) return '';
    
    const lines = content.split('\n');
    return lines.map(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
        return { type: 'bullet', text: trimmed.replace(/^[•*-]\s*/, '') };
      }
      return { type: 'paragraph', text: trimmed };
    }).filter(item => item.text);
  };

    const copyToClipboard = async (textToCopy, successMessage) => {
    const text = textToCopy || vacancyText; // Fallback naar volledige vacaturetekst
    const message = successMessage || 'Vacaturetekst is gekopieerd naar je klembord.';

    try {
        await navigator.clipboard.writeText(text);
        notify.showSuccessToast(
        'Gekopieerd!', 
        message
        );
    } catch (error) {
        notify.showErrorToast(
        'Kopiëren mislukt', 
        'Kon tekst niet kopiëren. Probeer het handmatig.'
        );
    }
    };

  const downloadAsPDF = () => {
    try {
      // NIEUWE PROFESSIONELE PDF GENERATOR
      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        putOnlyUsedFonts: true,
        compress: true
      });

      // Extract job title voor header
      const firstLine = vacancyText.split('\n')[0] || '';
      let jobTitle = firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine;
      jobTitle = jobTitle.replace(/\*\*/g, '').replace(/^Functietitel:\s*/i, '').trim();

      // === MODERNE HEADER DESIGN ===
      
      // Gradient achtergrond effect
      pdf.setFillColor(78, 84, 200);
      pdf.rect(0, 0, 210, 50, 'F');
      
      pdf.setFillColor(143, 148, 251);
      pdf.rect(0, 0, 210, 40, 'F');
      
      pdf.setFillColor(102, 217, 232);
      pdf.rect(140, 0, 70, 50, 'F');

      // Logo/Initialen cirkel
      pdf.setFillColor(255, 255, 255);
      pdf.circle(30, 25, 10, 'F');
      pdf.setTextColor(78, 84, 200);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('JG', 26.5, 29);

      // Hoofdtitel
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.text('VACATURETEKST', 50, 22);
      
      // Functie titel
      if (jobTitle) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(jobTitle.toUpperCase(), 50, 30);
      }

      // Datum
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      const today = new Date();
      const dateStr = today.toLocaleDateString('nl-NL', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      pdf.text(`Gegenereerd op ${dateStr}`, 50, 38);
      pdf.text('Jesse - Digital Recruiter', 50, 44);

      // === CONTENT CONTAINER ===
      
      // Witte content area
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(15, 60, 180, 200, 3, 3, 'F');
      
      // Subtiele border
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(15, 60, 180, 200, 3, 3);

      // === TEKST RENDERING ===
      
      let yPosition = 75;
      const leftMargin = 25;
      const contentWidth = 160;
      const lineHeight = 6;

      // Gebruik de al geparste vacature of parse de tekst
      const sectionsToRender = formattedVacancy ? 
        Object.entries(formattedVacancy) : 
        Object.entries(parseVacancyToSections(vacancyText));
      
      sectionsToRender.forEach(([sectionTitle, content], index) => {
        // Nieuwe pagina check
        if (yPosition > 240) {
          pdf.addPage();
          
          // Herhaal content container
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(15, 25, 180, 240, 3, 3, 'F');
          pdf.setDrawColor(226, 232, 240);
          pdf.setLineWidth(0.5);
          pdf.roundedRect(15, 25, 180, 240, 3, 3);
          
          yPosition = 40;
        }

        // Sectie header met moderne styling
        if (sectionTitle !== 'intro') {
          pdf.setFillColor(78, 84, 200);
          pdf.roundedRect(leftMargin - 3, yPosition - 6, contentWidth + 6, 10, 2, 2, 'F');
          
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text(sectionTitle.toUpperCase(), leftMargin, yPosition);
          yPosition += 15;
        }

        // Content
        pdf.setTextColor(45, 55, 72);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);

        // Verwerk content (string of al geformatteerde content)
        let contentText = typeof content === 'string' ? content : '';
        if (typeof content === 'object' && content.text) {
          contentText = content.text;
        }

        const paragraphs = contentText.split('\n').filter(p => p.trim());
        
        paragraphs.forEach(paragraph => {
          const trimmed = paragraph.trim();
          if (!trimmed) return;

          if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
            // Bullet point
            const bulletText = trimmed.replace(/^[•-]\s*/, '');
            
            // Modern bullet
            pdf.setFillColor(102, 217, 232);
            pdf.circle(leftMargin + 3, yPosition - 1, 1.5, 'F');
            
            const lines = pdf.splitTextToSize(bulletText, contentWidth - 10);
            pdf.text(lines, leftMargin + 8, yPosition);
            yPosition += lines.length * lineHeight + 3;
            
          } else {
            // Normale tekst
            const lines = pdf.splitTextToSize(trimmed, contentWidth);
            pdf.text(lines, leftMargin, yPosition);
            yPosition += lines.length * lineHeight + 5;
          }
        });
        
        yPosition += 8;
      });

      // === PROFESSIONELE FOOTER ===
      
      const pageCount = pdf.internal.getNumberOfPages();
      
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        
        // Footer lijn
        pdf.setDrawColor(78, 84, 200);
        pdf.setLineWidth(1);
        pdf.line(15, 275, 195, 275);
        
        // Footer tekst
        pdf.setTextColor(100, 116, 139);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        
        pdf.text('Gegenereerd door Jesse', 20, 285);
        pdf.text(`Pagina ${i} van ${pageCount}`, 190, 285, { align: 'right' });
        pdf.text('www.jessegeul.nl', 105, 285, { align: 'center' });
      }

      pdf.save('vacaturetekst.pdf');
      
      notify.showSuccessToast(
        'Professionele PDF gedownload!',
        'Je vacaturetekst is opgeslagen als professionele PDF.'
      );
    } catch (error) {
      notify.showErrorToast(
        'PDF fout',
        'Er ging iets mis bij het maken van de PDF.'
      );
    }
  };

  // VOEG ook deze helper functie toe aan VacancyReviewScreen.jsx als die er nog niet is:

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const newMessage = { role: 'user', content: chatInput.trim() };
    setChatMessages(prev => [...prev, newMessage]);
    setChatInput('');
    setIsLoading(true);

    try {
      const token = await getAccessTokenSilently();
      
      const response = await fetch('/api/vacancy-chat', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: chatInput.trim(),
          vacancyText: vacancyText,
          conversation: chatMessages
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply
      }]);

      // Als er een nieuwe versie is, update de vacancy
      if (data.updatedVacancy) {
        console.log('📝 Updated vacancy received from review chat');
        
        // Update local formatted vacancy
        const formatted = parseVacancyText(data.updatedVacancy);
        setFormattedVacancy(formatted);
        
        // Callback naar parent component
        if (onVacancyUpdated) {
          onVacancyUpdated({
            updatedText: data.updatedVacancy,
            source: 'vacancy_review_chat',
            originalText: vacancyText
          });
        }
        
        // Show success notification
        notify.showSuccessToast(
          'Vacature bijgewerkt!',
          'De wijzigingen zijn doorgevoerd in de vacaturetekst.'
        );
      }

    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, er ging iets mis. Probeer het opnieuw.'
      }]);
      
      notify.showErrorToast(
        'Chat fout',
        'Er ging iets mis bij het verwerken van je verzoek.'
      );
    }
    setIsLoading(false);
  };

  const copySectionToClipboard = async (sectionTitle, sectionContent) => {
    try {
        const formattedContent = formatContent(sectionContent);
        const textContent = `${sectionTitle}\n\n${formattedContent.map(item => 
        item.type === 'bullet' ? `• ${item.text}` : item.text
        ).join('\n')}`;
        
        await navigator.clipboard.writeText(textContent);
        notify.showSuccessToast(
        'Sectie gekopieerd!', 
        `"${sectionTitle}" is gekopieerd naar je klembord.`
        );
    } catch (error) {
        notify.showErrorToast(
        'Kopiëren mislukt', 
        'Kon sectie niet kopiëren. Probeer het handmatig.'
        );
    }
  };

  if (!isVisible) return null;

  // Empty state: geen of te weinig vacaturetekst
  const hasContent = formattedVacancy && Object.keys(formattedVacancy).length > 0;

  if (!hasContent) {
    return createPortal(
      <div className="vacancy-review-overlay">
        <div className="vacancy-review-container">
          <div className="vacancy-header">
            <div className="header-icon-badge header-icon-badge--empty">
              <span className="material-icons">edit_note</span>
            </div>
            <div className="header-content">
              <h1>Nog geen vacaturetekst</h1>
              <p>Jesse heeft nog niet genoeg informatie om een vacature te schrijven</p>
            </div>
            <button className="close-btn" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="vacancy-body">
            <div className="vacancy-empty-state">
              <div className="empty-state-icon">
                <span className="material-icons">description</span>
              </div>
              <h2>Start een gesprek met Jesse</h2>
              <p>
                Vertel Jesse over de functie die je wilt invullen. Na een kort gesprek
                schrijft hij een complete, professionele vacaturetekst voor je.
              </p>
              <div className="empty-state-steps">
                <div className="empty-step">
                  <span className="empty-step-number">1</span>
                  <span>Start een gesprek en vertel over de functie</span>
                </div>
                <div className="empty-step">
                  <span className="empty-step-number">2</span>
                  <span>Jesse stelt slimme vervolgvragen</span>
                </div>
                <div className="empty-step">
                  <span className="empty-step-number">3</span>
                  <span>Ontvang een kant-en-klare vacaturetekst</span>
                </div>
              </div>
              <button className="action-btn primary" onClick={onClose}>
                <span className="material-icons">arrow_back</span>
                Terug naar het gesprek
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className={`vacancy-review-overlay ${isAnimating ? 'animating' : ''}`}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(15px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        className="vacancy-review-container"
        style={{
          background: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '1400px',
          height: '90vh',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header — brand gradient + avatar */}
        <div className="vacancy-header">
          <img className="header-avatar" src={avatarImg} alt="Jesse" />
          <div className="header-content">
            <h1>Je vacaturetekst is klaar!</h1>
            <p>Bekijk, kopieer of pas aan waar nodig</p>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Sluiten">
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="vacancy-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Vacancy Display */}
          <div className="vacancy-display" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="vacancy-content" style={{ flex: 1, overflowY: 'auto' }}>
              {/* Intro sectie — extract title from first line */}
              {formattedVacancy.intro && (() => {
                const introItems = formatContent(formattedVacancy.intro);
                const titleItem = introItems[0];
                const restItems = introItems.slice(1);
                return (
                  <div className="vacancy-intro">
                    {titleItem && <h2 className="vacancy-title">{titleItem.text}</h2>}
                    {restItems.map((item, index) => (
                      <p key={index} className="intro-text">{item.text}</p>
                    ))}
                  </div>
                );
              })()}

              {/* Secties */}
              {Object.entries(formattedVacancy).map(([section, content]) => {
                if (section === 'intro') return null;
                return (
                  <div key={section} className="vacancy-section">
                    <div className="section-header-row">
                      <h3 className="section-header">{titleCase(section)}</h3>
                      <button
                        className="copy-section-btn"
                        onClick={() => copySectionToClipboard(section, content)}
                        title={`Kopieer "${titleCase(section)}"`}
                      >
                        <span className="material-icons">content_copy</span>
                      </button>
                    </div>
                    <div className="section-content">
                      {formatContent(content).map((item, index) => (
                        item.type === 'bullet' ? (
                          <div key={index} className="bullet-point">{item.text}</div>
                        ) : (
                          <p key={index}>{item.text}</p>
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action bar */}
            <div className="vacancy-actions">
              <button className="action-btn" onClick={() => copyToClipboard()}>
                <span className="material-icons">content_copy</span>
                Kopieer tekst
              </button>
              <button className="action-btn" onClick={downloadAsPDF}>
                <span className="material-icons">download</span>
                Download PDF
              </button>
              <button className="action-btn accent" onClick={toggleChatPanel}>
                <span className="material-icons">edit</span>
                {showChatPanel ? 'Sluit chat' : 'Aanpassen'}
              </button>
            </div>
          </div>

          {/* Draggable + Resizable Chat Panel */}
          <div
            ref={dragRef}
            className={`chat-panel ${showChatPanel ? 'visible' : ''}`}
            style={showChatPanel ? {
              transform: `translate(calc(-50% + ${chatPos.x}px), 0)`,
              height: `${chatHeight}px`,
            } : undefined}
          >
            {/* Resize handle — drag up/down to resize */}
            <div
              className="chat-resize-handle"
              onMouseDown={onResizeStart}
              onTouchStart={onResizeStart}
            />
            <div
              className="chat-header"
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              style={{ cursor: 'grab' }}
            >
              <h3>Verfijn met Jesse</h3>
              <button
                className="minimize-btn"
                onClick={closeChat}
                title="Sluit chat"
              >
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="chat-messages">
              {chatMessages.length === 0 && (
                <div className="chat-intro">
                  <div className="intro-message">
                    <div className="jesse-avatar"></div>
                    <div className="message-content">
                      <strong>Jesse</strong>
                      <p>Wat vind je van de vacaturetekst? Welke aanpassingen wil je maken?</p>
                      <div className="quick-suggestions">
                        <button onClick={() => setChatInput("Maak de intro pakkender")}>
                          Intro verbeteren
                        </button>
                        <button onClick={() => setChatInput("Voeg meer voordelen toe")}>
                          Meer voordelen
                        </button>
                        <button onClick={() => setChatInput("Eisen zijn te streng")}>
                          Eisen aanpassen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              
                {chatMessages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.role}`}>
                    <div className={`message-avatar ${msg.role}`}></div>
                    <div className="message-bubble-wrapper">
                    <div className="message-bubble">
                        <div className="message-header">
                        <strong>{msg.role === 'user' ? 'Jij' : 'Jesse'}</strong>
                        </div>
                        <div className="message-content">
                        {msg.role === 'assistant' ? (
                            <FormattedMessageContent content={msg.content} />
                        ) : (
                            <p>{msg.content}</p>
                        )}
                        </div>
                    </div>
                    {/* Kopieerknop voor AI berichten */}
                    {msg.role === 'assistant' && (
                        <button
                        className="chat-copy-btn"
                        title="Kopieer bericht"
                        onClick={() => copyToClipboard(msg.content, "Bericht gekopieerd!")}
                        >
                        <span className="material-icons">content_copy</span>
                        </button>
                    )}
                    </div>
                </div>
                ))}

              {isLoading && (
                <div className="chat-message assistant">
                  <div className="message-avatar assistant"></div>
                  <div className="message-content loading">
                    <strong>Jesse</strong>
                    <div className="typing-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <p>Denkt na over je aanpassing...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="chat-input">
              <div className="input-group">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Beschrijf welke aanpassing je wilt..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendChatMessage();
                    }
                  }}
                  disabled={isLoading}
                />
                <button 
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || isLoading}
                  className="send-btn"
                >
                  <span className="material-icons">send</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VacancyReviewScreen;